import { PackageSpec, Product, ProductVariant } from '../types';
import { applyChoices, bundleItemsFor, isBundle, resolveBundle } from './bundles';

/**
 * Vikt och mått för frakt. Produkten har standardvärden och varje variant
 * kan ha egna; saknas ett värde på varianten gäller produktens. Gränserna
 * är desamma som databasens kontroller (products_*_check).
 */
export const WEIGHT_LIMITS = { min: 1, max: 100_000 } as const;
export const CM_LIMITS = { min: 0.1, max: 300 } as const;

export const PACKAGE_KEYS = ['weightGrams', 'lengthCm', 'widthCm', 'heightCm'] as const;
export type PackageKey = typeof PACKAGE_KEYS[number];

/**
 * Tolkar ett fält. Tomt blir undefined. Komma fungerar som decimaltecken.
 * Vikten avrundas till hela gram och måtten till en decimal, som i databasen.
 */
export const parsePackageValue = (key: PackageKey, raw: string): number | undefined => {
    const trimmed = raw.trim().replace(',', '.');
    if (!trimmed) return undefined;
    const n = Number(trimmed);
    if (!Number.isFinite(n)) return undefined;
    return key === 'weightGrams' ? Math.round(n) : Math.round(n * 10) / 10;
};

/** Felet för ett värde, eller null om det är tomt eller giltigt. */
export const packageValueError = (key: PackageKey, value: number | undefined): string | null => {
    if (value === undefined) return null;
    if (key === 'weightGrams') {
        return value < WEIGHT_LIMITS.min || value > WEIGHT_LIMITS.max ? `Vikten ska vara ${WEIGHT_LIMITS.min}–${WEIGHT_LIMITS.max.toLocaleString('sv-SE')} g` : null;
    }
    return value < CM_LIMITS.min || value > CM_LIMITS.max ? `Måtten ska vara ${String(CM_LIMITS.min).replace('.', ',')}–${CM_LIMITS.max} cm` : null;
};

/** Första felet bland produktens och varianternas värden, för att stoppa sparandet i tid. */
export const firstPackageError = (product: PackageSpec, variants: ProductVariant[] = []): string | null => {
    for (const spec of [product, ...variants]) {
        for (const key of PACKAGE_KEYS) {
            const error = packageValueError(key, spec[key]);
            if (error) return error;
        }
    }
    return null;
};

/** Bara de ifyllda värdena, så att tomma fält inte sparas som nycklar i variantens JSON. */
export const cleanPackageSpec = <T extends PackageSpec>(spec: T): T => {
    const out = { ...spec };
    for (const key of PACKAGE_KEYS) if (out[key] === undefined || out[key] === null) delete out[key];
    return out;
};

/** Vad som gäller för en variant: variantens egna värden, annars produktens. */
export const packageSpecOf = (product: PackageSpec, variant?: PackageSpec): PackageSpec => {
    const spec: PackageSpec = {};
    for (const key of PACKAGE_KEYS) {
        const value = variant?.[key] ?? product[key];
        if (value !== undefined && value !== null) spec[key] = value;
    }
    return spec;
};

const cm = (n: number) => n.toLocaleString('sv-SE', { maximumFractionDigits: 1 });

/** "270 g · 16 × 9 × 5 cm". Tom sträng om inget är ifyllt. */
export const describePackage = (spec: PackageSpec): string => {
    const parts: string[] = [];
    if (spec.weightGrams !== undefined) parts.push(`${spec.weightGrams.toLocaleString('sv-SE')} g`);
    const dims = [spec.lengthCm, spec.widthCm, spec.heightCm];
    if (dims.every(d => d !== undefined)) parts.push(`${dims.map(d => cm(d!)).join(' × ')} cm`);
    return parts.join(' · ');
};

/** Varianter (eller produkten, utan varianter) som saknar vikt. Frakten kan inte räknas för dem. */
export const missingWeightCount = (product: Product): number => {
    const variants = product.variants || [];
    if (variants.length === 0) return product.weightGrams === undefined ? 1 : 0;
    return variants.filter(v => packageSpecOf(product, v).weightGrams === undefined).length;
};

/** Vikten i produktlistan: "270 g", "270–520 g", eller null när någon vikt saknas. */
export const weightRange = (product: Product): string | null => {
    if (missingWeightCount(product) > 0) return null;
    const variants = product.variants || [];
    const weights = variants.length > 0
        ? variants.map(v => packageSpecOf(product, v).weightGrams!)
        : [product.weightGrams!];
    const min = Math.min(...weights), max = Math.max(...weights);
    const g = (n: number) => n.toLocaleString('sv-SE');
    return min === max ? `${g(min)} g` : `${g(min)}–${g(max)} g`;
};

// --- Frakt i kassan -------------------------------------------------------

/**
 * Inställningarna för hemleverans (site_content "shipping_rates"). Själva
 * fraktpriset räknas i databasen (home_shipping_quote) efter Jonathans
 * fraktmodell, docs/frakt/: listpris efter vikt och ett golv som gör att
 * ingen order går minus. Kassan frågar quote_home_shipping.
 */
export interface ShippingRates {
    enabled: boolean;
    /** Används inte längre: hemleveranser skickas när gruppens säljperiod är slut (api/_lib/deliveryText.ts). */
    deliveryDays?: string;
}

export const DEFAULT_SHIPPING_RATES: ShippingRates = {
    enabled: true,
    deliveryDays: '2–4 vardagar',
};

/** Listpriserna inkl. moms (samma som home_shipping_quote). Golvet kan höja priset. */
export const SHIPPING_LIST_PRICES = [
    { label: 'Upp till 1 kg', food: 65, nonFood: 85 },
    { label: '1–3 kg', food: 85, nonFood: 105 },
    { label: 'Över 3 kg', food: 129, nonFood: 129 },
] as const;

export type ShippingQuote =
    | { ok: true; price: number; weightGrams: number }
    | { ok: false; reason: string };

/** Det databasen sparade på ordern när den lades (orders.shipping_quote). */
export interface ShippingQuoteSnapshot {
    price: number;
    listPrice: number;
    floorUsed: boolean;
    packaging: 'BAG' | 'BOX';
    goodsWeightGrams: number;
    weightGrams: number;
    service: 'HOME_SMALL' | 'HOME';
    /** PostNords beräknade kostnad exkl. moms, utan säkerhetsmarginal. */
    postnordCost: number;
    shippingExVat: number;
}

/**
 * Varukorgens vikt, eller namnen på det som saknar vikt (kan då inte
 * skickas hem). Variantens vikt gäller före produktens, som i databasen.
 */
export const cartWeight = (lines: { product: Product; variant?: ProductVariant; quantity: number; choices?: Record<string, string> }[], catalog: Product[] = []): { grams: number; missing: string[] } => {
    let grams = 0;
    const missing: string[] = [];
    for (const line of lines) {
        // Paket: vikten är summan av delarna (samma som order_line_snapshot).
        const parts = isBundle(line.product) ? resolveBundle(applyChoices(bundleItemsFor(line.product, line.variant), line.choices), catalog, []) : null;
        const w = parts
            ? (parts.length > 0 && parts.every(x => typeof x.weightGrams === 'number') ? parts.reduce((t, x) => t + (x.weightGrams as number) * x.quantity, 0) : undefined)
            : packageSpecOf(line.product, line.variant).weightGrams;
        if (w === undefined) missing.push(line.product.name);
        else grams += w * line.quantity;
    }
    return { grams, missing: Array.from(new Set(missing)) };
};

// --- Packning och bokning efter vägning (frakt_berakning.py) ---------------

/** PostNord Home Small: upp till 3 kg, längd högst 60 cm, längd + bredd + höjd högst 90 cm. */
export const HOME_SMALL_MAX_GRAMS = 3000;
export const HOME_SMALL_MAX_LENGTH_CM = 60;
export const HOME_SMALL_MAX_SUM_CM = 90;
/** PostNord Home: tilläggstjänsten Heavy krävs över 20 kg. */
export const HEAVY_FROM_GRAMS = 20000;
/** Viktstegen i Home Small, i gram. */
export const HOME_SMALL_STEPS_GRAMS = [500, 1000, 2000, 3000] as const;

export const PACKING_RULE = 'E-com-påse: 1–2 st kaffe/te/BARÚ, och Flaskan. Kartong: Glasen, och ordrar med 3 eller fler produkter.';

/** Kort instruktion på ordern i admin innan packning. */
export const packingInstruction = (estimatedGrams: number): string =>
    estimatedGrams <= HOME_SMALL_MAX_GRAMS
        ? `${PACKING_RULE} Max längd 60 cm, längd + bredd + höjd max 90 cm. Väg paketet och boka PostNord Home Small med den vägda vikten.`
        : 'Packa i kartong (standardlåda 41 × 41 × 41). Väg paketet och boka PostNord Home med den vägda vikten och lådans mått. Över 20 kg: välj tilläggstjänsten Heavy.';

export interface Booking {
    service: 'PostNord Home Small' | 'PostNord Home';
    /** Vad som ska bokas, t.ex. "PostNord Home Small, viktsteg upp till 0,5 kg". */
    book: string;
    warnings: string[];
}

const stepOf = (grams: number): number => grams > HOME_SMALL_MAX_GRAMS ? Infinity : HOME_SMALL_STEPS_GRAMS.find(s => grams <= s)!;
const kgText = (grams: number, decimals = 1) => (grams / 1000).toLocaleString('sv-SE', { minimumFractionDigits: decimals, maximumFractionDigits: 2 });

/**
 * Efter packning och vägning: vilken tjänst och vilket viktsteg som ska
 * bokas, så att paketet aldrig bokas fel (då tar PostNord ut straffavgifter).
 */
export const bookingAfterWeighing = (weighedGrams: number, lengthCm: number, widthCm: number, heightCm: number, estimatedGrams?: number): Booking => {
    const tooBigForSmall = lengthCm > HOME_SMALL_MAX_LENGTH_CM || lengthCm + widthCm + heightCm > HOME_SMALL_MAX_SUM_CM;
    const warnings: string[] = [];
    let service: Booking['service'];
    let book: string;
    if (weighedGrams <= HOME_SMALL_MAX_GRAMS && !tooBigForSmall) {
        service = 'PostNord Home Small';
        book = `${service}, viktsteg upp till ${kgText(stepOf(weighedGrams))} kg`;
    } else {
        service = 'PostNord Home';
        if (tooBigForSmall && weighedGrams <= HOME_SMALL_MAX_GRAMS) {
            warnings.push('Paketet är för stort för Home Small. Boka Home i stället, eller packa om i en mindre förpackning.');
        }
        const cm = (n: number) => n.toLocaleString('sv-SE', { maximumFractionDigits: 1 });
        book = `${service}, vikt ${kgText(weighedGrams, 2)} kg, mått ${cm(lengthCm)} × ${cm(widthCm)} × ${cm(heightCm)} cm`;
        if (weighedGrams > HEAVY_FROM_GRAMS) book += ', tilläggstjänst Heavy';
    }
    if (estimatedGrams !== undefined && stepOf(weighedGrams) > stepOf(estimatedGrams)) {
        warnings.push('Paketet väger mer än appen räknade med och hamnar i ett dyrare viktsteg. Boka ändå efter den vägda vikten, och kontrollera produktvikterna i appen.');
    }
    return { service, book, warnings };
};

/** "1,1 kg" eller "540 g". */
export const formatKg = (grams: number): string =>
    grams >= 1000
        ? `${(Math.round(grams / 100) / 10).toLocaleString('sv-SE')} kg`
        : `${grams.toLocaleString('sv-SE')} g`;

/** Tolkar sparade inställningar; trasiga värden ger standardvärdena. */
export const normalizeShippingRates = (raw: unknown): ShippingRates => {
    if (!raw || typeof raw !== 'object') return DEFAULT_SHIPPING_RATES;
    const r = raw as Partial<ShippingRates>;
    return {
        enabled: r.enabled !== false,
        deliveryDays: typeof r.deliveryDays === 'string' && r.deliveryDays.trim() ? r.deliveryDays.trim() : DEFAULT_SHIPPING_RATES.deliveryDays,
    };
};

/** Svenskt postnummer: fem siffror, första inte 0. Returnerar "553 20" eller null. */
export const normalizePostalCode = (raw: string): string | null => {
    const digits = raw.replace(/\s/g, '');
    return /^[1-9]\d{4}$/.test(digits) ? `${digits.slice(0, 3)} ${digits.slice(3)}` : null;
};
